import type { components } from '@social-studio/operator-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFailure, call } from '../../lib/api';

export type TenantDetail = components['schemas']['OperatorTenantDetailDto'];
export type TenantMetrics = components['schemas']['TenantMetricsDto'];
export type AuditEvent = components['schemas']['OperatorAuditEventDto'];

/** One tenant with its live metrics (REQ-140-012); the server audits every load. */
export function useTenant(id: string) {
  return useQuery({
    queryKey: ['operator-tenant', id],
    queryFn: () => call(() => api.GET('/v1/operator/tenants/{id}', { params: { path: { id } } })),
    // Each load is an audited read: no refetch on remount, no retry for a tenant that does not exist.
    staleTime: 5 * 60_000,
    retry: (failures, error) => !(error instanceof ApiFailure && error.status === 404) && failures < 1,
  });
}

/** The operator audit of one tenant, newest first (REQ-140-005). */
export function useTenantAudit(id: string) {
  return useInfiniteQuery({
    queryKey: ['operator-tenant-audit', id],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call(() => api.GET('/v1/operator/tenants/{id}/audit', { params: { path: { id }, query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
}

export type IndustryProfile = TenantDetail['industry_profile'];

/** Suspend, unsuspend and the industry profile (REQ-140-021, REQ-140-022); each refreshes the detail and its audit. */
export function useTenantActions(id: string) {
  const client = useQueryClient();
  const refresh = () =>
    Promise.all([client.invalidateQueries({ queryKey: ['operator-tenant', id] }), client.invalidateQueries({ queryKey: ['operator-tenant-audit', id] })]);
  const path = { params: { path: { id } } };
  return {
    suspend: useMutation({
      mutationFn: (reason: string) => call(() => api.POST('/v1/operator/tenants/{id}/suspension', { ...path, body: { reason } })),
      onSuccess: refresh,
    }),
    unsuspend: useMutation({
      mutationFn: (reason: string) => call(() => api.DELETE('/v1/operator/tenants/{id}/suspension', { ...path, body: { reason } })),
      onSuccess: refresh,
    }),
    changeIndustry: useMutation({
      mutationFn: ({ profile, reason }: { profile: IndustryProfile; reason: string }) =>
        call(() => api.PATCH('/v1/operator/tenants/{id}', { ...path, body: { industry_profile: profile, reason } })),
      onSuccess: refresh,
    }),
  };
}
